import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { buildWhatsAppUrl } from "../lib/whatsapp";

const CartDrawer = () => {
  const { items, drawerOpen, setDrawerOpen, removeItem, updateQty } = useCart();
  const { t, lang } = useLang();

  const handleRequestQuote = () => {
    const lines = items.map((it) => {
      const name = lang === "fr" ? it.name_fr : it.name_en;
      return `• ${name} (×${it.qty})`;
    });
    const message = [
      t.cart.quoteIntro,
      "",
      ...lines,
      "",
      t.cart.quoteOutro,
    ].join("\n");
    window.open(buildWhatsAppUrl(message), "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-[#FAF9F6] z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E1D8]">
              <h2 className="font-serif-display text-2xl">{t.cart.title}</h2>
              <button
                onClick={() => setDrawerOpen(false)}
                className="text-[#1A1A1A] hover:text-[#B34A36]"
                data-testid="cart-drawer-close"
                aria-label="Close cart"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center px-10 text-center gap-4">
                  <p className="text-[#5C5C5C]">{t.cart.empty}</p>
                  <Link
                    to="/shop"
                    onClick={() => setDrawerOpen(false)}
                    className="btn-primary"
                    data-testid="cart-empty-cta"
                  >
                    {t.cart.emptyCta}
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-[#E5E1D8]">
                  {items.map((it) => {
                    const name = lang === "fr" ? it.name_fr : it.name_en;
                    return (
                      <li
                        key={it.id}
                        className="px-6 py-5 flex gap-4"
                        data-testid={`cart-item-${it.slug}`}
                      >
                        <img
                          src={it.image}
                          alt={name}
                          className="w-20 h-24 object-cover bg-[#F2EFE9]"
                        />
                        <div className="flex-1 flex flex-col justify-between min-w-0">
                          <div>
                            <p className="font-serif-display text-base text-[#1A1A1A] truncate">
                              {name}
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center border border-[#E5E1D8]">
                              <button
                                onClick={() => updateQty(it.id, it.qty - 1)}
                                className="w-7 h-7 flex items-center justify-center hover:bg-[#F2EFE9]"
                                data-testid={`qty-dec-${it.slug}`}
                                aria-label="Decrease"
                              >
                                <Minus size={12} />
                              </button>
                              <span className="w-7 text-center text-sm" data-testid={`qty-${it.slug}`}>
                                {it.qty}
                              </span>
                              <button
                                onClick={() => updateQty(it.id, it.qty + 1)}
                                className="w-7 h-7 flex items-center justify-center hover:bg-[#F2EFE9]"
                                data-testid={`qty-inc-${it.slug}`}
                                aria-label="Increase"
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                            <button
                              onClick={() => removeItem(it.id)}
                              className="text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] hover:text-[#B34A36]"
                              data-testid={`remove-${it.slug}`}
                            >
                              {t.cart.remove}
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#E5E1D8] px-6 py-6 space-y-4">
                <button
                  onClick={handleRequestQuote}
                  className="btn-primary w-full justify-center"
                  data-testid="cart-checkout"
                >
                  {t.cart.checkout}
                </button>
                <p className="text-xs text-[#5C5C5C] text-center leading-relaxed">
                  {t.cart.checkoutNote}
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
